import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../lib/cn.ts';
import { formatLongDay, formatMonthYear } from '../lib/format.ts';
import { DATE_RANGE_LABEL, customRange, dayKey, formatDateRange, monthGrid, presetRange, startOfDay } from '../lib/dateRange.ts';
import type { DateRange, DateRangePreset } from '../lib/dateRange.ts';
import { Button } from './Button.tsx';
import { IconButton } from './IconButton.tsx';
import { Popover, PopoverContent, PopoverTrigger } from './Popover.tsx';
import { Pressable } from './Pressable.tsx';
import { Segmented } from './Segmented.tsx';

export interface DateRangePickerProps {
  /** null: no range (every day), shown as `anyLabel`. */
  value: DateRange | null;
  onChange(range: DateRange): void;
  /** The presets offered, in order (default all five). */
  presets?: readonly DateRangePreset[];
  /** Days before this cannot be picked (the first day with history). */
  minDay?: number;
  now?: number;
  /** The label while no range is chosen ("Any time"). */
  anyLabel?: string;
  className?: string;
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const monthTitle = formatMonthYear;
const dayLabel = formatLongDay;

/**
 * The one date range control (ADR 0109): Today, 7d, 30d, 90d and Custom as a segmented row,
 * and the selected span as a button that opens a month calendar (pick the first day, then the
 * last). The button's label is what the page header shows: "Oct 18 to Oct 24". Pages keep the
 * value in the URL (parseDateRange, writeDateRange).
 */
export function DateRangePicker({ value: given, onChange, presets = ['today', '7d', '30d', '90d', 'custom'], minDay, now = Date.now(), anyLabel = 'Any time', className }: DateRangePickerProps) {
  const value = given ?? { preset: '' as DateRangePreset, from: now, to: now };
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => startOfDay(value.to - 1));
  const [first, setFirst] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const today = startOfDay(now);
  const label = given ? formatDateRange(given, now) : anyLabel;

  const choose = (p: DateRangePreset) => {
    if (p === 'custom') {
      setMonth(startOfDay(value.to - 1));
      setFirst(null);
      setOpen(true);
      return;
    }
    onChange(presetRange(p, now));
  };

  const pick = (day: number) => {
    if (first == null) {
      setFirst(day);
      return;
    }
    onChange(customRange(first, day));
    setFirst(null);
    setOpen(false);
  };

  // What the grid highlights: the span being picked, or the selected one.
  const lo = first != null ? Math.min(first, hover ?? first) : given ? startOfDay(given.from) : Infinity;
  const hi = first != null ? Math.max(first, hover ?? first) : given ? startOfDay(given.to - 1) : -Infinity;
  const m = new Date(month);
  const shift = (n: number) => setMonth(new Date(m.getFullYear(), m.getMonth() + n, 1).getTime());

  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <Segmented<DateRangePreset> label="Date range" size="sm" options={presets.map((p) => ({ value: p, label: DATE_RANGE_LABEL[p] }))} value={value.preset} onChange={choose} className="hidden md:flex" />
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setFirst(null);
        }}
      >
        <PopoverTrigger asChild>
          <Button size="sm" variant="ghost" icon={<CalendarDays />} aria-label={`Date range: ${label}. Pick days`}>
            <span className="num">{label}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-3">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-1 md:hidden">
              {presets
                .filter((p) => p !== 'custom')
                .map((p) => (
                  <Button
                    key={p}
                    size="sm"
                    variant={value.preset === p ? 'secondary' : 'ghost'}
                    onClick={() => {
                      onChange(presetRange(p as Exclude<DateRangePreset, 'custom'>, now));
                      setOpen(false);
                    }}
                  >
                    {DATE_RANGE_LABEL[p]}
                  </Button>
                ))}
            </div>
            <div className="flex items-center justify-between gap-2">
              <IconButton size="sm" label="Previous month" icon={<ChevronLeft />} onClick={() => shift(-1)} />
              <span className="text-ui font-medium text-fg" aria-live="polite">
                {monthTitle(month)}
              </span>
              <IconButton size="sm" label="Next month" icon={<ChevronRight />} onClick={() => shift(1)} disabled={new Date(m.getFullYear(), m.getMonth() + 1, 1).getTime() > today} />
            </div>
            <div role="grid" aria-label={monthTitle(month)} className="grid grid-cols-7 gap-y-1" onMouseLeave={() => setHover(null)}>
              {WEEKDAYS.map((d) => (
                <span key={d} role="columnheader" className="flex h-control-xs items-center justify-center text-caption text-fg-subtle">
                  {d}
                </span>
              ))}
              {monthGrid(month)
                .flat()
                .map((day) => {
                  const inMonth = new Date(day).getMonth() === m.getMonth();
                  const disabled = day > today || (minDay != null && day < startOfDay(minDay));
                  const inSpan = day >= lo && day <= hi;
                  const edge = day === lo || day === hi;
                  return (
                    <Pressable
                      key={day}
                      role="gridcell"
                      aria-label={dayLabel(day)}
                      aria-selected={inSpan}
                      disabled={disabled}
                      onClick={() => pick(day)}
                      onMouseEnter={() => setHover(day)}
                      onFocus={() => setHover(day)}
                      className={cn(
                        'flex size-control-sm items-center justify-center text-center text-ui num',
                        inSpan ? 'bg-accent-soft text-fg' : 'rounded-control hover:bg-hover',
                        edge && 'rounded-control bg-accent text-accent-fg hover:bg-accent',
                        day === lo && !edge && 'rounded-l-control',
                        !inMonth && !inSpan && 'text-fg-subtle',
                        'disabled:text-fg-faint',
                        day === today && !edge && 'font-semibold',
                      )}
                    >
                      {new Date(day).getDate()}
                    </Pressable>
                  );
                })}
            </div>
            <p className="text-caption text-fg-subtle" aria-live="polite">
              {first == null ? 'Pick the first day' : `From ${dayKey(first)}: pick the last day`}
            </p>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
